<template>
  <div v-if="canShowApplePay" @click.prevent="startSession">
    <slot>Apple Pay</slot>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { storeTokenData } from './token-storage';
import { checkRequiredFields } from './validator';
import { isApplePayAvailable } from './sdk-loader';
import * as ApplePayApi from './apple-pay-api';

interface ClientContextIdHeader {
  [key: string]: string;
}

const props = defineProps<{
  amount: string | number;
  currency: string;
  countryCode?: string;
  locale?: string;
  invoice?: string | null;
  hasError?: boolean;
  clientContextIdHeader: ClientContextIdHeader;
}>();

const emit = defineEmits<{
  (event: 'payment-authorized'): void;
  (event: 'error', error: Error): void;
  (event: 'cancel'): void;
}>();

const canShowApplePay = ref(false);

const applePaySettings = {
  supportedNetworks: ['visa', 'masterCard'],
  merchantCapabilities: ['supports3DS'],
  label: 'Paybis',
  version: 3,
};

onMounted(async () => {
  const isFeatureEnabled = await ApplePayApi.isApplePayOneClickFeatureEnabled();
  if (!isFeatureEnabled) return;

  canShowApplePay.value = await isApplePayAvailable();
});

function startSession() {
  if (props.hasError) return;

  if (!props.amount) {
    emit('error', new Error('Amount is required for Apple Pay Payment.'));
    return;
  }

  const session = new ApplePaySession(applePaySettings.version, {
    countryCode: props.countryCode || 'US',
    currencyCode: props.currency,
    requiredBillingContactFields: ['postalAddress'],
    requiredShippingContactFields: ['email'],
    supportedNetworks: applePaySettings.supportedNetworks as ApplePaySupportedNetworks[],
    merchantCapabilities: applePaySettings.merchantCapabilities as ApplePayMerchantCapability[],
    total: {
      label: applePaySettings.label,
      amount: props.amount.toString(),
    },
  });

  session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
    try {
      const data = await ApplePayApi.validatePaymentSession(
        {
          validationUrl: event.validationURL,
          invoice: props.invoice ?? undefined,
        },
        props.clientContextIdHeader,
      );
      session.completeMerchantValidation(data);
    } catch (error: any) {
      emit('error', error);
      session.abort();
    }
  };

  const updateParams = {
    newTotal: {
      amount: props.amount.toString(),
      currency: props.currency,
      label: applePaySettings.label,
    },
  };

  session.onpaymentmethodselected = () => {
    session.completePaymentMethodSelection(updateParams);
  };

  session.onshippingmethodselected = () => {
    session.completeShippingMethodSelection(updateParams);
  };

  session.onshippingcontactselected = () => {
    session.completeShippingContactSelection(updateParams);
  };

  session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
    const { token, billingContact, shippingContact } = event.payment;

    const validationErrors = checkRequiredFields(billingContact || {});
    if (validationErrors) {
      const applePayErrors = validationErrors.map((err: any) => {
        return new ApplePayError('billingContactInvalid', err.contactField, err.message);
      });

      session.completePayment({
        status: ApplePaySession.STATUS_FAILURE,
        errors: applePayErrors,
      });
      return;
    }

    if (!shippingContact?.emailAddress) {
      session.completePayment({
        status: ApplePaySession.STATUS_FAILURE,
        errors: [
          new ApplePayError('shippingContactInvalid', 'emailAddress', 'Email is required.'),
        ],
      });
      return;
    }

    try {
      const { data } = await ApplePayApi.storeApplePayToken(token.paymentData);

      const tokenData = {
        tokenId: data.token_id,
        expiresAt: data.expires_at,
        timestamp: Date.now(),
        email: shippingContact.emailAddress,
      };

      const billingAddress = {
        cardholderName: `${billingContact?.givenName || ''} ${billingContact?.familyName || ''}`.trim(),
        address: (billingContact?.addressLines || []).map((line: string) => line.trim()).filter(Boolean).join(', '),
        city: billingContact?.locality?.trim() || '',
        country: billingContact?.countryCode || '',
        state:
            billingContact?.countryCode === 'US'
              ? billingContact?.administrativeArea?.trim()
              : null,
        zip: billingContact?.postalCode?.trim() || '',
      };

      const [clientContextId] = Object.values(props.clientContextIdHeader);
      storeTokenData({ ...tokenData, ...billingAddress, clientContextId });

      session.completePayment(ApplePaySession.STATUS_SUCCESS);
      emit('payment-authorized');
    } catch (error: any) {
      emit('error', error);
      session.completePayment(ApplePaySession.STATUS_FAILURE);
    }
  };

  session.oncancel = () => {
    emit('cancel');
  };

  session.begin();
}
</script>
